import { cn } from '@/utils/cn';
import { calendarDateToIso, isoToCalendarDate } from '@/utils/dateTime';
import { getBcp47Locale } from '@/utils/locale';
import type { CalendarDate } from '@internationalized/date';
import { typographyVariants } from '@packages/webshop-design-system';
import {
  forwardRef,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  DateInput as AriaDateInput,
  DateField,
  DateSegment,
  I18nProvider,
} from 'react-aria-components';
import { MIN_DATE } from './dateConstants';

export interface DateInputProps {
  /** ISO date-time string; empty or undefined when no date is set */
  value?: string;
  /** Receives the ISO value once a complete valid date is entered, '' otherwise */
  onChange: (iso: string) => void;
  onBlur?: () => void;
  /** Earliest date that may be committed (defaults to 1900-01-01) */
  minValue?: CalendarDate;
  /** Latest date that may be committed (defaults to no upper bound) */
  maxValue?: CalendarDate;
  /** Picker trigger rendered inside the field, e.g. a calendar icon button */
  endAdornment?: ReactNode;
  error?: boolean;
  classNames?: { wrapper?: string; input?: string };
  'aria-label'?: string;
  'data-testid'?: string;
  id?: string;
  'aria-describedby'?: string;
}

// Manual date entry via react-aria's segmented DateField (DD.MM.YYYY order in
// every supported locale). Keeps its own segment state so partial input
// survives; the form only ever sees a complete in-range ISO date or an empty
// string.
export const DateInput = forwardRef<HTMLDivElement, DateInputProps>(
  function DateInput(
    {
      value,
      onChange,
      onBlur,
      minValue = MIN_DATE,
      maxValue,
      endAdornment,
      error,
      classNames,
      ...props
    },
    ref
  ) {
    const externalDate = useMemo(
      () => isoToCalendarDate(value) ?? null,
      [value]
    );
    const [date, setDate] = useState<CalendarDate | null>(externalDate);

    // Reflect programmatic value changes (calendar pick, async form reset) in
    // the segments; when the value is merely cleared, keep in-progress input.
    useEffect(() => {
      if (externalDate) {
        setDate((prev) =>
          prev && prev.compare(externalDate) === 0 ? prev : externalDate
        );
      }
    }, [externalDate]);

    const handleChange = (next: CalendarDate | null) => {
      setDate(next);
      const isAllowed =
        next &&
        next.compare(minValue) >= 0 &&
        (!maxValue || next.compare(maxValue) <= 0);
      onChange(isAllowed ? calendarDateToIso(next) : '');
    };

    return (
      <I18nProvider locale={getBcp47Locale()}>
        <div className={cn('relative flex items-center', classNames?.wrapper)}>
          <DateField<CalendarDate>
            ref={ref}
            value={date}
            onChange={handleChange}
            onBlur={onBlur}
            minValue={minValue}
            maxValue={maxValue}
            isInvalid={error || undefined}
            shouldForceLeadingZeros
            // Out-of-range values must not block the submit natively —
            // react-hook-form owns validation and shows the field error.
            validationBehavior="aria"
            className="w-full"
            {...props}
          >
            <AriaDateInput
              className={cn(
                typographyVariants({ variant: 'body1' }),
                // Mirrors the design-system Input styling, with focus styles
                // on the group since focus lives inside the segments.
                'flex h-10 w-full items-center rounded-sm border border-neutral-medium bg-white px-3 py-2 text-dark hover:border-primary-medium',
                'focus-within:border-primary focus-within:ring-1 focus-within:ring-primary',
                {
                  ['border-error-text focus-within:border-error-text focus-within:ring-error-text hover:border-error-text']:
                    error,
                },
                classNames?.input
              )}
            >
              {(segment) => (
                <DateSegment
                  segment={segment}
                  className="rounded-sm px-0.5 tabular-nums caret-transparent outline-none data-[type=literal]:px-0 data-[placeholder]:text-neutral focus:bg-primary focus:text-white"
                />
              )}
            </AriaDateInput>
          </DateField>
          {endAdornment && (
            <div className="absolute right-2">{endAdornment}</div>
          )}
        </div>
      </I18nProvider>
    );
  }
);
